import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'
import moment from 'moment'

import { DoctorsController } from '@App/api'
import { type IApiErrorHandler } from '@App/types/common'
import {
    type IDoctorFilters,
    type IDoctor,
    type TDoctorsPageMode,
    type IUnpublishedFeedback,
    type IUpdateFeedback,
    type IFeedbacksDatesFilter
} from '@App/types/doctorsType'
import { onHandleResponseNotification, onUseOdataQueries2 } from '@App/utils'
import { BLANC_IMAGE_URL, defaultDoctorsFilters } from '@Config/config'

import { doctorState } from '..'

import { type IGetDoctorsListParams } from './helper'

class Doctors {
    list: IDoctor[] | null = null
    feedbacksDatesFilter: IFeedbacksDatesFilter = {
        startDate: moment().utc().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().utc().format('YYYY-MM-DD')
    }

    unhandledDoctors: number | null = null
    unpublishedFeedbacks: IUnpublishedFeedback[] | null = null
    totalCount: number = 0
    isFetchingData: boolean = false
    filters: IDoctorFilters = defaultDoctorsFilters

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    onSetFeedbacksDatesFilter(feedbacksDatesFilter: IFeedbacksDatesFilter) {
        this.feedbacksDatesFilter = feedbacksDatesFilter
    }

    onSetDoctorsList(list: IDoctor[] | null) {
        this.list = list
    }

    onSetUnpublishedFeedbacks(unpublishedFeedbacks: IUnpublishedFeedback[] | null) {
        this.unpublishedFeedbacks = unpublishedFeedbacks
    }

    onSetUnhandledDoctors(unhandledDoctors: number | null) {
        this.unhandledDoctors = unhandledDoctors
    }

    onSetTotalCount(count: number) {
        this.totalCount = count
    }

    onSetIsFetchingData(isFetching: boolean) {
        this.isFetchingData = isFetching
    }

    onSetFilters(filters: IDoctorFilters) {
        this.filters = filters
    }

    onUseQueryFilters() {
        const { search, photo, post, description } = this.filters
        const PHOTO_FILTER_MAPPER: Record<TDoctorsPageMode, string> = {
            handled: `avatar ne '${BLANC_IMAGE_URL}' and avatar ne null`,
            unhandled: `avatar eq '${BLANC_IMAGE_URL}' or avatar eq null`,
            all: 'avatar eq any[]'
        }

        const DESCRIPTION_FILTER_MAPPER: Record<TDoctorsPageMode, string> = {
            handled: 'hasDescription eq true',
            unhandled: 'hasDescription eq false',
            all: 'hasDescription eq any()'
        }

        const searchFilter = `(contains(tolower(iname),'${search.toLowerCase()}') or contains(tolower(fname),'${search.toLowerCase()}') or contains(tolower(oname),'${search.toLowerCase()}') or contains(tolower(post),'${search.toLowerCase()}'))`
        const avatarFilter = PHOTO_FILTER_MAPPER[photo]
        const postFilter = `(contains(tolower(post),'${post ? post.toLowerCase() : ''}'))`
        const descriptionFilter = DESCRIPTION_FILTER_MAPPER[description]
        return onUseOdataQueries2({
            filters: [
                { filterString: searchFilter, isExist: search.length > 0, conditionParam: 'and' },
                { filterString: avatarFilter, isExist: photo !== 'all', conditionParam: 'and' },
                { filterString: postFilter, isExist: !!post, conditionParam: 'and' },
                { filterString: descriptionFilter, isExist: description !== 'all' }
            ],
            sortindFiled: 'fname asc',
            limit: this.filters.limit,
            offset: this.filters.offset,
            count: true
        })
    }

    // requests
    async onGetDoctorsList(params: IGetDoctorsListParams = {}) {
        this.onSetIsFetchingData(true)
        const { isAdditional = false } = params
        const client = new DoctorsController()
        try {
            const { data } = await client.onGetDoctorsList(this.onUseQueryFilters())
            if (isAdditional && this.list) {
                this.onSetDoctorsList([...this.list, ...data.value])
            } else {
                this.onSetDoctorsList(data.value)
                this.onSetTotalCount(data['@odata.count'])
            }
            this.onSetFilters({ ...this.filters, offset: (this.filters.offset += 1) })
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        } finally {
            this.onSetIsFetchingData(false)
        }
    }

    onGetUnpublishedFeedbacks = async () => {
        const dates = this.feedbacksDatesFilter
        const startDate = dates.startDate ? `${dates.startDate}T00:00:00.000Z` : null
        const endDate = dates.endDate ? `${dates.endDate}T23:00:00.000Z` : null

        const datesFilter = `(createdAt gt ${startDate} and createdAt lt ${endDate})`

        const queries = onUseOdataQueries2({
            filters: [{ filterString: datesFilter, isExist: !!startDate && !!endDate }]
        })

        const client = new DoctorsController()
        try {
            const { data } = await client.onGetUnpublishedFeedbacks(queries)
            this.onSetUnpublishedFeedbacks(data.value)
        } catch (e) {
            this.onSetUnpublishedFeedbacks(null)
        }
    }

    async onPublishFeedback(dto: IUpdateFeedback) {
        doctorState.onSetIsFetchingFeedbacks(true)
        const client = new DoctorsController()
        const { status } = await client.onUpdateFeedback(dto)
        try {
            if (status >= 200 && status < 300 && this.unpublishedFeedbacks) {
                this.onSetUnpublishedFeedbacks(this.unpublishedFeedbacks.filter((feedback) => feedback.id !== dto.id))
                doctorState.onSetIsFetchingFeedbacks(false)
            }
        } catch (e) {
            doctorState.onSetIsFetchingFeedbacks(false)
        } finally {
            doctorState.onSetIsFetchingFeedbacks(false)
        }
    }
}

export default new Doctors()
